import { Component, AfterContentChecked, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Course } from '@shared/models/course.model';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { CurrentContextService } from '@shared/services/currentContextService';
import { AssetType, Annotation } from '@shared/models';
import { LinkService } from '@shared/services/link.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { filter } from 'rxjs/internal/operators/filter';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { GenericConfirmationDialogComponent } from '@shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { SupervisorService } from '@shared/services/supervisor.service';
import { EmbedDialogComponent } from '@shared/components/embed-dialog/embed-dialog.component';

@Component({
  selector: 'simulation-edit',
  templateUrl: './simulation-edit.component.html',
  styleUrls: ['./simulation-edit.component.scss']
})
export class SimulationEditComponent implements AfterContentChecked {
  public courseId: string;
  public course: Course;
  public npc: Annotation;
  public createMode = false;
  public AT = AssetType;
  public title: string;
  private visitedSteps = new Set<number>();

  @ViewChild('stepper') stepper: MatStepper;
  private isChildExpanded = false;
  constructor(
    private _courseApiService: CoursesAPIService,
    private _currentContext: CurrentContextService,
    private _dialog: MatDialog,
    private _linkService: LinkService,
    private _route: ActivatedRoute,
    private _router: Router,
    private _supervisorService: SupervisorService
  ) {
    this.courseId = this._route.snapshot.params.courseId;
    this.createMode = this._route.snapshot.queryParams.create;
    this._supervisorService.reloadCourseObs.pipe(
      switchMap(() => {
        return this._courseApiService
          .getCourseById(this.courseId)
      })).subscribe(course => this.course = course);
      
    this._courseApiService
      .getCourseMainNPC(this.courseId)
      .toPromise()
      .then((annotation) => {
        this.npc = annotation;
      });
  }

  ngAfterContentChecked(): void {
    //stepper is defined only after content init
    this._route.queryParams.subscribe((params) => {
      if (params.step != undefined) {
        this.goToStep(params.step);
      } else {
        this.visitedSteps.add(0);
      }
    });
  }
  openShareMenu() {
    this._dialog
      .open(EmbedDialogComponent, {
        panelClass: ['learnspace-theme'],
        data: { course: this.course, styleClass: 'learnspace-theme' },
        autoFocus: false
      })
      .afterClosed()
      .subscribe();
  
  }
  cancel() {
    if (this.createMode) {
      this._dialog
        .open(GenericConfirmationDialogComponent, {
          width: '450px',
          panelClass: ['learnspace-theme'],
          data: {
            title: 'Discard current draft?',
            message: 'All the changes you made to this simulation template won’t be saved.',
            proceedText: 'DISCARD',
            abortText: 'BACK TO EDITING',
            dangerMode: true
          }
        })
        .afterClosed()
        .pipe(
          filter((val) => val === 'ok'),
          switchMap(() => {
            return this._courseApiService.deleteCourse(this.course.id);
          })
        )
        .subscribe(() => this.goToParentProgram());
    } else {
      this.goToParentProgram();
    }
  }

  onSelectionChanged(event: any) {
    this.changeUrlParams(event);
    this.visitedSteps.add(event.selectedIndex);
  }

  changeUrlParams(event: any) {
    const params: Params = { step: event.selectedIndex };
    this._router.navigate([], {
      relativeTo: this._route,
      queryParams: params,
      queryParamsHandling: ''
    });
  }

  hasAllInRange(set: Set<number>, start: number, end: number): boolean {
    if (end == undefined) return set.has(start);
    for (let k = start; k <= end; k++) {
      if (!set.has(k)) return false;
    }
    return true;
  }

  defineListItemStyle(index: number, indexEnd?: number) {
    if (!this.stepper) return "";
    if (indexEnd == undefined && this.stepper.selectedIndex === index || (this.stepper.selectedIndex >= index && this.stepper.selectedIndex <= index)) {
      return "step--active";
    }
    if (!this.createMode) {
      return "step--visited";
    }
    if (this.hasAllInRange(this.visitedSteps, index, indexEnd)) {
      return "step--visited";
    }
    return "step--not-visited";
  }

  getTitle(): string {
    if (this.createMode) {
      return 'Create a New Training';
    } else {
      return `Edit: ${this.course.title}`;
    }
  }

  goToParentProgram() {
    this._router.navigate(['../..'], { relativeTo: this._route });
  }
  onCourseChange(event: Course) {
    this.course = { ...this.course, ...event };
  }
  onNPCChange(event: Annotation) {
    this.npc = event;
  }
  goToStep(index: number) {
    if (!this.stepper) return;
    this.stepper.selectedIndex = index;
  }

  updateSkybox(event: any) {
    this._courseApiService.updateCourse(this.course.id, { skybox_id: event.id }, false)
      .subscribe((course) => {
        this._supervisorService.triggerGeneratePoster(course);
        this.course = course;
      });
  }

  updateNpc(event: any) {
    this._courseApiService.updateAnnotation(this.npc.id, { media_id: event.id })
      .subscribe((npc) => {
        this.npc = npc;
        this._supervisorService.triggerGeneratePoster(this.course);
      });
  }
  onExpandChange(expandStatus: boolean) {
    this.isChildExpanded = expandStatus;
  }
}
